import {
  getCoverageMesh,
  drawCoveragePoints,
  updateCoveragePoints,
} from "./dellyCoverageBlueprint";
import { COVERAGE_CL_DELLY } from "../../constants/options";
import CoveragePoint from "./models/DellyCoveragePoint";
import getCoverageTicks from "./getCoverageTicks";
import ColorScale from "../../models/ColorScale";
import Filter from "../../../../models/Filter";

function getDefaultState(track) {
  const { sampleId, analysisId, originAnalId } = track.getSharedState();

  return {
    covPoints: [],
    covMap: new Map(),
    sampleId,
    analysisId,
    originAnalId,
    visRange: [0, 1],
    sampleFilters: [],
    scaleFilter: new Filter({
      label: "Logarithmic",
      category: "Y Axis",
      key: "logScale",
      isVisible: true,
      totalCount: null,
      value: true,
      type: "toggle",
    }),
    globalMax: 1,
    max: 1,
    colorInd: {},
    colorMap: [],
  };
}

function dellyCoverageTrackController(track) {
  let state = getDefaultState(track);
  const coverageMesh = getCoverageMesh();

  function onReset() {
    state = getDefaultState(track);
  }

  function onInit() {
    track.setFilters([state.scaleFilter]);
  }

  function onRangeChange({ chrSegments }) {
    const params = {
      analysisId: state.analysisId,
      sampleId: state.sampleId,
    };

    track.loadPosition(chrSegments, params);
  }

  function onDataReceived({ res, chr }) {
    const { covMap, globalMax } = state;
    const {
      coverages: c,
      chr_start_absolute_pos: absStart,
      positions,
    } = res || {};

    const p = (positions || []).map((x) => x - (absStart || 0));
    const covs = (c || []).map((x, i) => new CoveragePoint(p[i], x, chr));
    const newCovs = covs.filter((x) => covMap.get(x.region.g0) === undefined);
    const max = newCovs.reduce((a, i) => (a > i.val ? a : i.val), globalMax);

    newCovs.forEach((x) => covMap.set(x.region.g0, x.val));

    state.covPoints = state.covPoints.concat(newCovs);

    state.globalMax = Math.max(globalMax, max);

    coverageMesh.setItems(state.covPoints);
    drawCoveragePoints(newCovs, coverageMesh);
    updateCoveragePoints(state.covPoints, coverageMesh, isLogScale());

    track.setOverlayProps({ points: state.covPoints });

    updateAxis();
    track.requestRender();
  }

  function calcMax() {
    const [x0, x1] = state.visRange;
    const visLen = x1 - x0;
    let max = 0;

    if (visLen < 50000) {
      for (let l = x0; l < x1; l++) {
        const val = state.covMap.get(l) || 0;
        if (val > max) max = val;
      }
    }
    return Math.max(1, max || state.globalMax);
  }

  function updateAxis() {
    const isLog = isLogScale();
    const max = calcMax();

    state.max = isLog ? Math.log10(max) : max;
    state.min = isLog ? -1 : 0;

    coverageMesh.uniforms.setMinY(state.max).setMaxY(state.min);
    const ticks = getCoverageTicks(state.min, state.max, isLog);

    track.setAxisProps({ ticks });
    track.requestRender();
  }

  function isLogScale() {
    return state.scaleFilter.value;
  }

  function applyFilters() {
    const isLog = isLogScale();
    updateCoveragePoints(state.covPoints, coverageMesh, isLog);
    track.requestRender();
  }

  function onFiltersChange(updated) {
    state.scaleFilter = updated.find((x) => x.key === "logScale");
    track.setFilters([state.scaleFilter]);
    setTimeout(() => {
      applyFilters();
      updateAxis();
    }, 100);
  }

  function onRangeStabilized({ chrSegments }) {
    const x0 = Math.floor(chrSegments[0]?.g0 || 0);
    const x1 = Math.floor(chrSegments[chrSegments.length - 1]?.g1 || 1) + 1;
    state.visRange[0] = x0;
    state.visRange[1] = x1;
    updateAxis();
  }

  function onPreferencesChanged() {
    const config = track.getPreferences();
    state.config = config;
    const { isDark } = config;
    const scaleName = config[COVERAGE_CL_DELLY];
    const theme = isDark ? 2 : 1;
    const colorScale = new ColorScale(scaleName, 5)
      .setTheme(theme)
      .setOpacity(0.3);

    coverageMesh.uniforms.setColorMap(colorScale.vectors);
    track.requestRender();
  }

  return {
    onReset,
    onPreferencesChanged,
    onInit,
    onRangeChange,
    onFiltersChange,
    onRangeStabilized,
    onDataReceived,
    meshes: [coverageMesh],
  };
}

export default dellyCoverageTrackController;
